import { Icon, Button, Badge } from '@/components/atoms';

export interface CartItem {
  variantId: string;
  productName: string;
  productSku: string;
  quantity: number;
  price: number; // Current active price (retail or member based on selection)
  retailPrice: number; // Retail price
  memberPrice?: number; // Member price (optional, may not exist)
  mspPrice?: number; // MSP price (optional)
  availableStock: number;
  gst_rate?: number; // GST rate for this item
  cgst_rate?: number; // Central GST rate
  sgst_rate?: number; // State GST rate
}

interface SalesCartProps {
  items: CartItem[];
  onUpdateQuantity: (variantId: string, quantity: number) => void;
  onRemoveItem: (variantId: string) => void;
  onClearCart: () => void;
  onCheckout: () => void;
  warehouseId: string;
}

export const SalesCart: React.FC<SalesCartProps> = ({
  items,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onCheckout,
}) => {
  // Calculate cart totals
  const subtotal = items.reduce((sum, item) => sum + (item.price ?? 0) * item.quantity, 0);
  
  // Calculate CGST and SGST based on items' GST rates
  // CGST and SGST are calculated as (gst_rate / 2) of the item total
  const cgstAmount = items.reduce((sum, item) => {
    const itemTotal = (item.price ?? 0) * item.quantity;
    const cgstRate = item.cgst_rate ?? 0;
    return sum + (itemTotal * cgstRate) / 100;
  }, 0);
  
  const sgstAmount = items.reduce((sum, item) => {
    const itemTotal = (item.price ?? 0) * item.quantity;
    const sgstRate = item.sgst_rate ?? 0;
    return sum + (itemTotal * sgstRate) / 100;
  }, 0);
  
  const taxAmount = cgstAmount + sgstAmount;
  const total = subtotal + taxAmount;

  // Check if any item has insufficient stock
  const hasInsufficientStock = items.some((item) => item.quantity > item.availableStock);

  // Format currency
  const formatPrice = (price: number | undefined | null) => {
    if (price === undefined || price === null || isNaN(price)) {
      return '₹0.00';
    }
    return `₹${price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm border border-neutral-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-neutral-200">
        <div className="flex items-center gap-2">
          <Icon name="ShoppingCart" size={24} className="text-primary-600" />
          <h2 className="text-xl font-bold text-neutral-900">Cart</h2>
          {items.length > 0 && (
            <Badge variant="primary">
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </Badge>
          )}
        </div>
        {items.length > 0 && (
          <button
            onClick={onClearCart}
            className="text-sm text-error-600 hover:text-error-700 font-medium flex items-center gap-1"
          >
            <Icon name="Trash2" size={16} />
            Clear
          </button>
        )}
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="bg-neutral-100 rounded-full p-6 mb-4">
              <Icon name="ShoppingCart" size={48} className="text-neutral-400" />
            </div>
            <p className="text-lg font-medium text-neutral-600 mb-1">Cart is empty</p>
            <p className="text-sm text-neutral-500">Search and add products to start a sale</p>
          </div>
        ) : (
          items.map((item) => {
            const itemPrice = item.price ?? 0;
            const itemTotal = itemPrice * item.quantity;
            const hasStockIssue = item.quantity > item.availableStock;

            return (
              <div
                key={item.variantId}
                className={`bg-neutral-50 rounded-lg p-4 border-2 transition-all ${
                  hasStockIssue ? 'border-error-300 bg-error-50' : 'border-neutral-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-neutral-900 mb-1">{item.productName}</h4>
                    <p className="text-sm text-neutral-600">SKU: {item.productSku}</p>
                    {hasStockIssue && (
                      <div className="flex items-center gap-1 mt-2">
                        <Icon name="CircleAlert" size={14} className="text-error-600" />
                        <p className="text-xs text-error-600 font-medium">
                          Only {item.availableStock} available in stock
                        </p>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => onRemoveItem(item.variantId)}
                    className="text-neutral-400 hover:text-error-600 transition-colors p-1"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        onUpdateQuantity(item.variantId, Math.max(1, item.quantity - 1))
                      }
                      disabled={item.quantity <= 1}
                      className="w-8 h-8 rounded-lg bg-white border border-neutral-300 flex items-center justify-center hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Icon name="Minus" size={16} className="text-neutral-700" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        onUpdateQuantity(item.variantId, Math.max(1, value));
                      }}
                      className="w-16 h-8 text-center border border-neutral-300 rounded-lg font-semibold text-neutral-900"
                    />
                    <button
                      onClick={() => onUpdateQuantity(item.variantId, item.quantity + 1)}
                      className="w-8 h-8 rounded-lg bg-white border border-neutral-300 flex items-center justify-center hover:bg-neutral-100 transition-colors"
                    >
                      <Icon name="Plus" size={16} className="text-neutral-700" />
                    </button>
                  </div>

                  {/* Item Total */}
                  <div className="text-right">
                    <p className="text-xs text-neutral-500">
                      {formatPrice(item.price)} × {item.quantity}
                    </p>
                    <p className="text-lg font-bold text-neutral-900">{formatPrice(itemTotal)}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Cart Summary */}
      {items.length > 0 && (
        <>
          <div className="border-t border-neutral-200 p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-neutral-600">Subtotal</span>
              <span className="font-semibold text-neutral-900">{formatPrice(subtotal)}</span>
            </div>
            {taxAmount > 0 && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-600">CGST</span>
                  <span className="font-semibold text-neutral-900">{formatPrice(cgstAmount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-600">SGST</span>
                  <span className="font-semibold text-neutral-900">{formatPrice(sgstAmount)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between text-lg font-bold border-t border-neutral-200 pt-3">
              <span className="text-neutral-900">Total</span>
              <span className="text-primary-600">{formatPrice(total)}</span>
            </div>
          </div>

          {/* Checkout Button */}
          <div className="p-4 bg-neutral-50 border-t border-neutral-200">
            <Button
              variant="primary"
              onClick={onCheckout}
              disabled={hasInsufficientStock || items.length === 0}
              className="w-full py-3 text-lg font-semibold"
            >
              <Icon name="CreditCard" size={20} className="mr-2" />
              Proceed to Checkout
            </Button>
            {hasInsufficientStock && (
              <p className="text-xs text-error-600 text-center mt-2">
                Please adjust quantities before checkout
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
